import type { InsertTakenSetups, Setup } from '@hvf/contracts'
import { useSetups, userId } from '@stateApp'
import { createMutation, createQuery, useQueryClient } from '@tanstack/solid-query'
import { createEffect, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { useChannelsReads } from '../../../../state/channelReads/state'
import {
  getAverageScore,
  getLeaderScore,
  getSetupPotentialHigQuality,
  getSetupSubscribedCount,
  getSetupTakenCount,
  handleGetSetup,
  handleGetSetupTaken,
  toggleSetupTaken,
} from './resource'

export type SetupView = 'analysis' | 'discussion'

export function useSingle() {
  const queryClient = useQueryClient()
  const { channelReadsQuery } = useService(useChannelsReads)()
  const { setUpdateSetup, updateSetupResource, setDeleteSetup } =
    useService(useSetups)()
  const [setupId, setSetupId] = createSignal<string | undefined>()
  const [activeView, setActiveView] = createSignal<SetupView>('analysis')
  const [isScoreOpen, setIsScoreOpen] = createSignal(false);

  const setupQuery = createQuery(() => ({
    queryKey: ['setup', setupId()],
    queryFn: () => handleGetSetup(setupId()!),
    enabled: !!setupId(),
  }))

  const takenQuery = createQuery(() => ({
    queryKey: ['setupTaken', setupId(), userId()],
    queryFn: () => handleGetSetupTaken(setupId(), userId()),
    enabled: !!setupId() && !!userId(),
  }))

  const takenAverageScoreQuery = createQuery(() => ({
    queryKey: ['takenAverageScore', setupId()],
    queryFn: () => getAverageScore(setupId()!),
    enabled: !!setupId(),
  }))

  const leaderScoreQuery = createQuery(() => ({
    queryKey: ['leaderScore', setupId()],
    queryFn: () => getLeaderScore(setupId()!),
    enabled: !!setupId(),
  }))

  const setupPotentialHigQualityQuery = createQuery(() => ({
    queryKey: ['setupPotentialHighQuality', setupId()],
    queryFn: () => getSetupPotentialHigQuality(setupId()!),
    enabled: !!setupId(),
  }))

  const takenCountQuery = createQuery(() => ({
    queryKey: ['takenCount', setupId()],
    queryFn: () => getSetupTakenCount(setupId()!),
    enabled: !!setupId(),
  }))

  const subscribedCountQuery = createQuery(() => ({
    queryKey: ['subscribedCount', setupQuery.data?.channel_id],
    queryFn: () => getSetupSubscribedCount(setupQuery.data?.channel_id!),
    enabled: !!setupQuery.data?.channel_id,
  }))

  const upsertTakenMutation = createMutation(() => ({
    mutationFn: toggleSetupTaken,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['setupTaken', setupId(), userId()],
      })
      queryClient.invalidateQueries({
        queryKey: ['takenCount', setupId()],
      })
    },
  }))

  const subscribed = () => {

    return channelReadsQuery.data?.find(
      (channelRead) => {
        return channelRead.channel_id === setupQuery.data?.channel_id
      },
    )?.is_subscribed
  }

  const toggleTaken = async () => {
    const has_taken = Boolean(!takenQuery.data?.has_taken)
    const setup_id = setupId()
    const user_id = userId()

    if (setup_id && user_id) {
      upsertTakenMutation.mutate({ setup_id, user_id, has_taken })
    }
  }

  const setScore = async (score: number) => {
    const has_taken = Boolean(takenQuery.data?.has_taken)
    const setup_id = setupId()
    const user_id = userId()

    if (setup_id && user_id) {
      upsertTakenMutation.mutate({ setup_id, user_id, has_taken, score })
    }
  }

  createEffect(() => {
    if (updateSetupResource.state === 'ready') {
      queryClient.invalidateQueries({ queryKey: ['setup', setupId()] })
    }
  })

  const onArchiveSetup = () => {
    if (setupQuery.data) {
      const setup: Setup = setupQuery.data
      setUpdateSetup({ ...setup, status: 'archived' })
    }
  }

  const onDeleteSetup = () => {
    if (setupQuery.data) {
      const setup: Setup = setupQuery.data
      setDeleteSetup(setup.id)
    }
  }

  const isArchived = () => setupQuery.data?.status === 'archived'

  return {
    isArchived,
    onArchiveSetup,
    setSetupId,
    setScore,
    setupId,

    setupQuery,
    takenQuery,
    takenAverageScoreQuery,
    leaderScoreQuery,
    setupPotentialHigQualityQuery,
    upsertTakenMutation,

    toggleTaken,
    subscribed,
    onDeleteSetup,
    activeView,
    setActiveView,

    isScoreOpen, setIsScoreOpen,

    takenCountQuery,
    subscribedCountQuery,
  }
}
